import React, { createContext, useContext, useState, useEffect } from 'react';
import { WAX_DIP_SURCHARGE, getProduct, isWaxDippable } from './products';

export interface CartItem {
  id: number;
  name: string;
  price: number;
  image: string;
  quantity: number;
  waxDip: boolean;
}

interface CartContextType {
  cart: CartItem[];
  addToCart: (item: Omit<CartItem, 'quantity'>) => void;
  removeFromCart: (id: number) => void;
  updateQuantity: (id: number, quantity: number) => void;
  updateWaxDip: (id: number, waxDip: boolean) => void;
  clearCart: () => void;
  getTotal: () => number;
  getItemCount: () => number;
}

// Bumping the suffix retires every previously saved cart. Do this whenever
// prices or product ids change, so a returning visitor cannot check out
// against a catalog that no longer exists.
const STORAGE_KEY = 'tampabeesupply-cart-v2';
const LEGACY_STORAGE_KEYS = ['tampabeesupply-cart'];
const MAX_QUANTITY = 999;

export const unitPrice = (item: Pick<CartItem, 'price' | 'waxDip'>): number =>
  item.price + (item.waxDip ? WAX_DIP_SURCHARGE : 0);

/**
 * Rebuild a persisted cart against the current catalog. Anything whose id has
 * disappeared is dropped, and prices are always taken from the catalog rather
 * than from storage, so a stale entry can never carry an old price forward.
 */
const reconcile = (raw: unknown): CartItem[] => {
  if (!Array.isArray(raw)) return [];

  const items: CartItem[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue;

    const product = getProduct((entry as CartItem).id);
    if (!product) continue;

    const quantity = Number((entry as CartItem).quantity);
    if (!Number.isInteger(quantity) || quantity < 1) continue;

    items.push({
      id: product.id,
      name: product.name,
      price: product.price,
      image: product.images[0],
      quantity: Math.min(quantity, MAX_QUANTITY),
      waxDip: (entry as CartItem).waxDip === true && product.waxDippable
    });
  }
  return items;
};

const loadCart = (): CartItem[] => {
  try {
    LEGACY_STORAGE_KEYS.forEach(key => localStorage.removeItem(key));
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? reconcile(JSON.parse(saved)) : [];
  } catch {
    // Corrupt JSON, or storage blocked entirely (Safari private mode).
    // An unguarded throw here would white-screen the whole site, homepage
    // included, with no way for the visitor to recover.
    return [];
  }
};

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [cart, setCart] = useState<CartItem[]>(loadCart);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(cart));
    } catch {
      // Quota exceeded or storage unavailable — the in-memory cart still works.
    }
  }, [cart]);

  const addToCart = (item: Omit<CartItem, 'quantity'>) => {
    setCart(prev => {
      const existing = prev.find(i => i.id === item.id);
      if (existing) {
        return prev.map(i =>
          i.id === item.id
            ? { ...i, quantity: Math.min(i.quantity + 1, MAX_QUANTITY) }
            : i
        );
      }
      return [...prev, { ...item, quantity: 1, waxDip: item.waxDip === true && isWaxDippable(item.id) }];
    });
  };

  const removeFromCart = (id: number) => {
    setCart(prev => prev.filter(i => i.id !== id));
  };

  const updateQuantity = (id: number, quantity: number) => {
    if (!Number.isFinite(quantity) || quantity <= 0) {
      removeFromCart(id);
      return;
    }
    const clamped = Math.min(Math.floor(quantity), MAX_QUANTITY);
    setCart(prev => prev.map(i => (i.id === id ? { ...i, quantity: clamped } : i)));
  };

  const updateWaxDip = (id: number, waxDip: boolean) => {
    if (!isWaxDippable(id)) return;
    setCart(prev => prev.map(i => (i.id === id ? { ...i, waxDip } : i)));
  };

  const clearCart = () => {
    setCart([]);
  };

  const getTotal = () =>
    Number(cart.reduce((sum, item) => sum + unitPrice(item) * item.quantity, 0).toFixed(2));

  const getItemCount = () => cart.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <CartContext.Provider value={{ cart, addToCart, removeFromCart, updateQuantity, updateWaxDip, clearCart, getTotal, getItemCount }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};
